import type * as React from 'react';
import { BarChart3, ChevronDownIcon, ExternalLinkIcon, Smartphone, Trash2Icon } from 'lucide-react';

import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { FieldLabel } from '../../../components/ui/field';
import { Input } from '../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Spinner } from '../../../components/ui/spinner';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../../components/ui/collapsible';
import { formatSlideNumber } from '../lib/format-number';
import type { PostSlide } from '../lib/build-post-slides';
import type { ManualPost } from '../model/usePresentationPosts';
import { ImageField } from './image-field';

/**
 * Карточки публикаций под полем со ссылками.
 *
 * Найденную показываем и не трогаем — по ней всё уже есть. Ненайденную
 * заполняют здесь же: скриншот статистики, с которого модель снимает цифры,
 * и картинка публикации. Раньше такие публикации выпадали из отчёта и их
 * добивали в PowerPoint руками.
 */

function CardShell({
  children,
  onRemove,
  disabled,
}: {
  children: React.ReactNode;
  onRemove: () => void;
  disabled: boolean;
}): React.ReactElement {
  return (
    <div className="relative rounded-md border p-3">
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2"
        aria-label="Убрать публикацию"
        onClick={onRemove}
        disabled={disabled}
      >
        <Trash2Icon />
      </Button>
      {children}
    </div>
  );
}

function PostLink({ url }: { url: string }): React.ReactElement {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="text-muted-foreground inline-flex items-center gap-1 text-xs hover:underline"
    >
      {url.replace(/^https?:\/\/(www\.)?/, '')}
      <ExternalLinkIcon className="size-3" />
    </a>
  );
}

export function FoundPostCard({
  slide,
  onRemove,
  disabled,
}: {
  slide: PostSlide;
  onRemove: () => void;
  disabled: boolean;
}): React.ReactElement {
  return (
    <CardShell onRemove={onRemove} disabled={disabled}>
      <div className="flex items-center gap-3 pr-8">
        {slide.imageUrl ? (
          <img
            src={`/api/presentation/image-proxy?url=${encodeURIComponent(slide.imageUrl)}`}
            alt=""
            className="size-12 shrink-0 rounded object-cover"
          />
        ) : (
          <div className="bg-muted size-12 shrink-0 rounded" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-medium">@{slide.username}</p>
            <Badge variant="secondary">{slide.badge}</Badge>
          </div>
          <PostLink url={slide.url} />
        </div>
        <div className="text-right text-sm">
          <p className="font-medium tabular-nums">{formatSlideNumber(slide.views)}</p>
          <p className="text-muted-foreground text-xs tabular-nums">
            охваты {formatSlideNumber(slide.reach)}
          </p>
        </div>
      </div>
    </CardShell>
  );
}

const EXTRA_FIELDS: Array<{ key: keyof ManualPost; label: string }> = [
  { key: 'likes', label: 'Лайки' },
  { key: 'comments', label: 'Комментарии' },
  { key: 'reposts', label: 'Репосты' },
  { key: 'shares', label: 'Отправки' },
  { key: 'saved', label: 'Сохранения' },
  { key: 'profileVisits', label: 'Посещения профиля' },
  { key: 'follows', label: 'Подписки' },
  { key: 'avgWatchSeconds', label: 'Среднее время, с' },
];

export function ManualPostCard({
  post,
  index,
  onPatch,
  onScreen,
  onRemove,
  disabled,
}: {
  post: ManualPost;
  index: number;
  onPatch: (patch: Partial<ManualPost>) => void;
  onScreen: (file: File) => void;
  onRemove: () => void;
  disabled: boolean;
}): React.ReactElement {
  const busy = disabled || post.isReading;
  const id = `manual-${post.shortcode}`;
  return (
    <CardShell onRemove={onRemove} disabled={disabled}>
      <div className="space-y-3">
        <div className="pr-8">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium">Публикация {index + 1}</p>
            <Badge variant="outline">Нет статистики</Badge>
            {post.isReading && (
              <span className="text-muted-foreground inline-flex items-center gap-1 text-xs">
                <Spinner className="size-3" />
                читаю скриншот…
              </span>
            )}
          </div>
          <PostLink url={post.url} />
          <p className="text-muted-foreground mt-1 text-xs">{post.reason}</p>
        </div>

        {/*
          Два поля с разной судьбой: из первого мы берём только цифры и саму
          картинку выбрасываем, второе целиком уезжает в макет. Раньше это были
          два одинаковых квадрата, отличимых лишь подписью, — и люди путали,
          куда вставлять что. Теперь у каждого свой значок и подпись говорит,
          что с картинкой станет.
        */}
        <div className="grid gap-3 sm:grid-cols-2">
          <ImageField
            id={`${id}-screen`}
            label="Скрин статистики — для цифр"
            hint="Ctrl+V. В слайд не попадёт"
            icon={BarChart3}
            file={post.screen}
            onChange={(file) => (file ? onScreen(file) : onPatch({ screen: null }))}
            disabled={busy}
          />
          <ImageField
            id={`${id}-photo`}
            label="Превью видео — в слайд"
            hint="Встанет в телефон на макете"
            icon={Smartphone}
            file={post.photo}
            onChange={(photo) => onPatch({ photo })}
            disabled={busy}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-4">
          <div className="space-y-1 sm:col-span-2">
            <FieldLabel htmlFor={`${id}-username`}>Паблик</FieldLabel>
            <Input
              id={`${id}-username`}
              placeholder="qazaq.press"
              value={post.username}
              onChange={(e) => onPatch({ username: e.target.value })}
              disabled={busy}
            />
          </div>
          <div className="space-y-1">
            <FieldLabel>Тип</FieldLabel>
            <Select
              value={post.badge}
              onValueChange={(value) => onPatch({ badge: (value as ManualPost['badge']) ?? 'Пост' })}
              disabled={busy}
            >
              <SelectTrigger className="w-full">
                <SelectValue>{post.badge}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Пост">Пост</SelectItem>
                <SelectItem value="Reels">Reels</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <FieldLabel htmlFor={`${id}-views`}>Просмотры</FieldLabel>
            <Input
              id={`${id}-views`}
              inputMode="numeric"
              value={post.views}
              onChange={(e) => onPatch({ views: e.target.value })}
              disabled={busy}
            />
          </div>
          <div className="space-y-1">
            <FieldLabel htmlFor={`${id}-reach`}>Охваты</FieldLabel>
            <Input
              id={`${id}-reach`}
              inputMode="numeric"
              value={post.reach}
              onChange={(e) => onPatch({ reach: e.target.value })}
              disabled={busy}
            />
          </div>
        </div>

        <Collapsible>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-1 px-2">
              <ChevronDownIcon />
              Остальные цифры экрана
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="grid gap-3 pt-3 sm:grid-cols-4">
            {EXTRA_FIELDS.map((field) => (
              <div className="space-y-1" key={field.key}>
                <FieldLabel htmlFor={`${id}-${field.key}`}>{field.label}</FieldLabel>
                <Input
                  id={`${id}-${field.key}`}
                  inputMode="numeric"
                  value={post[field.key] as string}
                  onChange={(e) => onPatch({ [field.key]: e.target.value } as Partial<ManualPost>)}
                  disabled={busy}
                />
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>
      </div>
    </CardShell>
  );
}
